/**
 * SwingPanel / SwingCell getData для layout.level=panel:
 * видимые fillable дети + Identificator; Label и hidden — пусто.
 * Не путать с isLayoutScopedFieldKey (там любой `_` prefix, в т.ч. `_deleted`).
 */
import { collectFormLayoutElements, collectHiddenPanelIds } from '@/common/utils/validateFormElementNames'
import { LAYOUT_IDENTITY_FIELD_KEYS } from '@/common/utils/layoutScopedClientFields'
import { shouldPreserveFieldEntryOnEmptyIncoming, fieldEntryHasPresentableValue, fieldEntryLooksEmptyIncoming } from '@/common/utils/changedDataFieldMerge'
import { readTilesRowScopeFromCtx } from '@/common/utils/tilesRowPanelData'

/** putFormData.md §7.2 — leaf getData = empty UniData. */
const EMPTY_PANEL_GET_DATA_ELEMENT_TYPES = new Set([
  'LABEL',
  'PROGRESSBAR',
  'LINEDELIMITER',
  'REPORT',
  'WORD',
  'WEBBROWSER',
  'SWTBROWSER',
  'VIDEOPLAYER',
  'CUSTOMVISUALENTITY',
  'EMPTYVISUALENTITY',
  'CONDITIONALPANEL',
  'SVG',
])

export type PanelGetDataElementHint = {
  fieldName?: string
  elementType?: string
  isVisible?: boolean
  doNotSerialize?: boolean
}

function normalizeElementType(type: unknown): string {
  return String(type ?? '')
    .trim()
    .toUpperCase()
}

export function isEmptyPanelGetDataElementType(type: unknown): boolean {
  return EMPTY_PANEL_GET_DATA_ELEMENT_TYPES.has(normalizeElementType(type))
}

export function isPanelGetDataIdentityKey(key: string): boolean {
  return LAYOUT_IDENTITY_FIELD_KEYS.has(String(key ?? '').trim())
}

export function isIdentificatorElementType(type: unknown): boolean {
  return normalizeElementType(type) === 'IDENTIFICATOR'
}

function readRefLikeBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value
  if (typeof value === 'function') {
    try {
      return readRefLikeBoolean((value as () => unknown)())
    } catch {
      return undefined
    }
  }
  if (value && typeof value === 'object' && 'value' in (value as object)) {
    const inner = (value as { value: unknown }).value
    if (typeof inner === 'boolean') return inner
  }
  return undefined
}

/** isVisible с registry: boolean / ref / getContext(). */
export function resolveRegisteredElementVisible(element: unknown): boolean | undefined {
  if (!element || typeof element !== 'object') return undefined
  const rec = element as Record<string, unknown>
  const direct = readRefLikeBoolean(rec.isVisible)
  if (direct !== undefined) return direct
  const getContext = rec.getContext
  if (typeof getContext === 'function') {
    try {
      const ctx = getContext()
      if (ctx && typeof ctx === 'object' && 'isVisible' in (ctx as object)) {
        return readRefLikeBoolean((ctx as { isVisible?: unknown }).isVisible)
      }
    } catch {
      return undefined
    }
  }
  return undefined
}

export function readPanelGetDataElementHint(element: unknown): PanelGetDataElementHint {
  if (!element || typeof element !== 'object') return {}
  const rec = element as Record<string, unknown>
  const typeRaw =
    rec.elementType ??
    (typeof rec.getType === 'function' ? (rec.getType as () => unknown)() : undefined)
  const fieldName = String(rec.fieldName ?? rec.name ?? '').trim()
  return {
    fieldName: fieldName || undefined,
    elementType: normalizeElementType(typeRaw) || undefined,
    isVisible: resolveRegisteredElementVisible(element),
    doNotSerialize: rec.doNotSerialize === true,
  }
}

/** SwingCell: invisible skip; SwingLabel empty; Identificator всегда (нет визуала). */
export function shouldIncludeElementInPanelGetData(hint: PanelGetDataElementHint): boolean {
  if (hint.doNotSerialize) return false
  if (isEmptyPanelGetDataElementType(hint.elementType)) return false
  if (isIdentificatorElementType(hint.elementType)) return true
  if (hint.isVisible === false) return false
  return true
}

export function findRegisteredPanelChild(
  elements: Record<string, unknown> | null | undefined,
  options: { panelId: string | number; elementId: string | number; formId?: string | number },
): Record<string, unknown> | null {
  if (!elements || typeof elements !== 'object') return null
  const panelId = String(options.panelId ?? '').trim()
  const elementId = String(options.elementId ?? '').trim()
  if (!panelId || !elementId) return null
  const formId = options.formId != null ? String(options.formId).trim() : ''
  const seen = new Set<object>()
  let fallback: Record<string, unknown> | null = null
  for (const el of Object.values(elements)) {
    if (!el || typeof el !== 'object') continue
    if (seen.has(el)) continue
    seen.add(el)
    const rec = el as Record<string, unknown>
    if (String(rec.panelId ?? '').trim() !== panelId) continue
    if (String(rec.elementId ?? '').trim() !== elementId) continue
    if (formId && rec.formId != null && String(rec.formId).trim() !== '' && String(rec.formId) !== formId) {
      if (fallback == null) fallback = rec
      continue
    }
    return rec
  }
  return fallback
}

function collectEmptyGetDataFieldNamesForPanels(
  formRoot: unknown,
  panelIds: Set<string>,
): Set<string> {
  const names = new Set<string>()
  if (panelIds.size === 0) return names
  for (const element of collectFormLayoutElements(formRoot)) {
    const panelId = String(element.panelId ?? '').trim()
    if (!panelId || !panelIds.has(panelId)) continue
    if (!isEmptyPanelGetDataElementType(element.type)) continue
    const name = String(element.name ?? '').trim()
    if (name) names.add(name)
  }
  return names
}

function collectHiddenFieldNamesForPanels(
  elements: Record<string, unknown> | null | undefined,
  panelIds: Set<string>,
): Set<string> {
  const names = new Set<string>()
  if (!elements || panelIds.size === 0) return names
  const seen = new Set<object>()
  for (const el of Object.values(elements)) {
    if (!el || typeof el !== 'object') continue
    if (seen.has(el)) continue
    seen.add(el)
    const rec = el as Record<string, unknown>
    const panelId = String(rec.panelId ?? '').trim()
    if (!panelId || !panelIds.has(panelId)) continue
    const hint = readPanelGetDataElementHint(el)
    if (isIdentificatorElementType(hint.elementType)) continue
    if (hint.isVisible !== false) continue
    if (hint.fieldName) names.add(hint.fieldName)
  }
  return names
}

function collectDoNotSerializeFieldNamesForPanels(
  elements: Record<string, unknown> | null | undefined,
  panelIds: Set<string>,
): Set<string> {
  const names = new Set<string>()
  if (!elements || panelIds.size === 0) return names
  const seen = new Set<object>()
  for (const el of Object.values(elements)) {
    if (!el || typeof el !== 'object') continue
    if (seen.has(el)) continue
    seen.add(el)
    const rec = el as Record<string, unknown>
    const panelId = String(rec.panelId ?? '').trim()
    if (!panelId || !panelIds.has(panelId)) continue
    const hint = readPanelGetDataElementHint(el)
    if (!hint.doNotSerialize) continue
    if (hint.fieldName) names.add(hint.fieldName)
  }
  return names
}

function collectPanelLayoutNames(formRoot: unknown, panelIds: Set<string>): Set<string> {
  const names = new Set<string>()
  if (panelIds.size === 0) return names
  for (const element of collectFormLayoutElements(formRoot)) {
    const panelId = String(element.panelId ?? '').trim()
    if (!panelId || !panelIds.has(panelId)) continue
    const name = String(element.name ?? '').trim()
    if (name) names.add(name)
  }
  return names
}

/** Snapshot-ключ в panel currentData: layout панели без Label/hidden; `_` только IDENTITY. */
export function isPanelGetDataSnapshotFieldKey(
  key: string,
  options: {
    layoutNames: Set<string>
    skipFieldNames: Set<string>
  },
): boolean {
  const normalized = String(key ?? '').trim()
  if (!normalized) return false
  if (isPanelGetDataIdentityKey(normalized)) return true
  if (options.skipFieldNames.has(normalized)) return false
  return options.layoutNames.has(normalized)
}

function collectShellHiddenFieldNamesForPanels(
  formRoot: unknown,
  panelIds: Set<string>,
  shellVisibleByElementId?: Record<string, boolean> | null,
): Set<string> {
  const names = new Set<string>()
  if (!shellVisibleByElementId || panelIds.size === 0) return names
  for (const element of collectFormLayoutElements(formRoot)) {
    const panelId = String(element.panelId ?? '').trim()
    if (!panelId || !panelIds.has(panelId)) continue
    if (isIdentificatorElementType(element.type)) continue
    const id = element.id != null ? String(element.id).trim() : ''
    if (!id || shellVisibleByElementId[id] !== false) continue
    const name = String(element.name ?? '').trim()
    if (name) names.add(name)
  }
  return names
}

function readShellVisibleMap(
  shellVisibleByElementId?: Record<string, boolean> | { value?: Record<string, boolean> } | null,
): Record<string, boolean> | null {
  if (!shellVisibleByElementId || typeof shellVisibleByElementId !== 'object') return null
  if (
    'value' in shellVisibleByElementId &&
    shellVisibleByElementId.value &&
    typeof shellVisibleByElementId.value === 'object' &&
    !Array.isArray(shellVisibleByElementId.value)
  ) {
    return shellVisibleByElementId.value
  }
  return shellVisibleByElementId as Record<string, boolean>
}

export type PanelGetDataCollectOptions = {
  elements?: Record<string, unknown> | null
  /** v-show shell (`en.setVisible("element", name, false)`). */
  shellVisibleByElementId?: Record<string, boolean> | { value?: Record<string, boolean> } | null
}

export function filterPanelGetDataSnapshotFields(
  fields: Record<string, unknown>,
  formRoot: unknown,
  panelIds: Set<string>,
  options?: PanelGetDataCollectOptions,
): Record<string, unknown> {
  const layoutNames = collectPanelLayoutNames(formRoot, panelIds)
  const shellMap = readShellVisibleMap(options?.shellVisibleByElementId)
  const skipFieldNames = new Set<string>([
    ...collectEmptyGetDataFieldNamesForPanels(formRoot, panelIds),
    ...collectHiddenFieldNamesForPanels(options?.elements, panelIds),
    ...collectDoNotSerializeFieldNamesForPanels(options?.elements, panelIds),
    ...collectShellHiddenFieldNamesForPanels(formRoot, panelIds, shellMap),
  ])
  const scoped: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(fields || {})) {
    if (isPanelGetDataSnapshotFieldKey(key, { layoutNames, skipFieldNames })) {
      scoped[key] = value
    }
  }
  return scoped
}

export function collectPanelIdentificatorFieldNames(
  formRoot: unknown,
  panelIds: Set<string>,
  elements?: Record<string, unknown> | null,
): Set<string> {
  const names = new Set<string>()
  if (panelIds.size === 0) return names
  for (const element of collectFormLayoutElements(formRoot)) {
    const panelId = String(element.panelId ?? '').trim()
    if (!panelId || !panelIds.has(panelId)) continue
    if (!isIdentificatorElementType(element.type)) continue
    const name = String(element.name ?? '').trim()
    if (name) names.add(name)
  }
  if (!elements) return names
  const seen = new Set<object>()
  for (const el of Object.values(elements)) {
    if (!el || typeof el !== 'object') continue
    if (seen.has(el)) continue
    seen.add(el)
    const rec = el as Record<string, unknown>
    const panelId = String(rec.panelId ?? '').trim()
    if (!panelId || !panelIds.has(panelId)) continue
    const hint = readPanelGetDataElementHint(el)
    if (!isIdentificatorElementType(hint.elementType)) continue
    if (hint.fieldName) names.add(hint.fieldName)
  }
  return names
}

export function pickIdentificatorFieldsFromSources(
  names: Set<string>,
  sources: Array<Record<string, unknown> | null | undefined>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const name of names) {
    for (const source of sources) {
      if (!source || typeof source !== 'object') continue
      if (!Object.prototype.hasOwnProperty.call(source, name)) continue
      const value = source[name]
      if (value === undefined || value === null) continue
      out[name] = value
      break
    }
  }
  return out
}

/** PK документа: `_id` / `_versionId` / `_typeId` из полного снимка страницы (в т.ч. chain). */
export function pickPanelIdentityFields(
  fields: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (!fields) return out
  for (const key of LAYOUT_IDENTITY_FIELD_KEYS) {
    if (key in fields && fields[key] != null) {
      out[key] = fields[key]
    }
  }
  return out
}

/** Identificator.getData панели (pageFields / forms slot), без panel getForm. */
export function collectPanelIdentificatorGetDataFields(
  elements: Record<string, unknown> | null | undefined,
  options: { panelId: string | number; formId?: string | number },
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (!elements) return out
  const panelId = String(options.panelId ?? '').trim()
  if (!panelId) return out
  const seen = new Set<object>()
  for (const el of Object.values(elements)) {
    if (!el || typeof el !== 'object') continue
    if (seen.has(el)) continue
    seen.add(el)
    const rec = el as Record<string, unknown>
    if (String(rec.panelId ?? '').trim() !== panelId) continue
    const hint = readPanelGetDataElementHint(el)
    if (!isIdentificatorElementType(hint.elementType)) continue
    const getData = rec.getData
    if (typeof getData !== 'function') continue
    const data = getData.call(el)
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      Object.assign(out, data as Record<string, unknown>)
    }
  }
  return out
}

const FORM_LISTBOX_GET_DATA_TYPES = new Set(['LISTBOX', 'COMBOBOX'])

export type FormLevelGetDataCollectOptions = {
  formId?: string | number
  /** pageData.form — панели `<hidden>` (SwingFormHidden.getData = empty). */
  formRoot?: unknown
  extraMaps?: Array<Record<string, unknown> | null | undefined>
}

const FORM_TEXT_GET_DATA_TYPES = new Set(['TEXT', 'PASSWORD', 'TEXTAREA', 'MULTILINETEXT'])

function flattenFormLeafElements(
  elements: Record<string, unknown> | null | undefined,
  extraMaps?: Array<Record<string, unknown> | null | undefined>,
): unknown[] {
  const seen = new Set<object>()
  const out: unknown[] = []
  const pushMap = (map: Record<string, unknown> | null | undefined) => {
    if (!map) return
    for (const el of Object.values(map)) {
      if (!el || typeof el !== 'object') continue
      if (seen.has(el)) continue
      seen.add(el)
      out.push(el)
    }
  }
  pushMap(elements)
  if (extraMaps) {
    for (const map of extraMaps) pushMap(map)
  }
  return out
}

function registryEntryMatchesFormId(
  rec: Record<string, unknown>,
  formId?: string | number,
): boolean {
  if (formId == null || formId === '' || formId === -1) return true
  const actual = String(rec.formId ?? '').trim()
  if (!actual) return true
  return actual === String(formId)
}

function resolveHiddenPanelIds(formRoot?: unknown): Set<string> | null {
  if (formRoot == null) return null
  const ids = collectHiddenPanelIds(formRoot)
  return ids.size > 0 ? ids : null
}

/** SwingFormHidden empty; Tiles clone getData → строка таблицы, не корень формы. */
function shouldSkipFormLevelLeafGetData(
  rec: Record<string, unknown>,
  hiddenPanelIds: Set<string> | null,
): boolean {
  if (readTilesRowScopeFromCtx(rec.ctx) || readTilesRowScopeFromCtx(rec)) {
    return true
  }
  if (!hiddenPanelIds || hiddenPanelIds.size === 0) return false
  const panelId = String(rec.panelId ?? '').trim()
  return Boolean(panelId && hiddenPanelIds.has(panelId))
}

function assignGetDataMap(
  out: Record<string, unknown>,
  data: unknown,
): void {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return
  Object.assign(out, data as Record<string, unknown>)
}

function assignPresentableGetDataMap(
  out: Record<string, unknown>,
  data: unknown,
): void {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return
  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    if (fieldEntryLooksEmptyIncoming(value)) continue
    if (out[key] != null && fieldEntryHasPresentableValue(out[key])) continue
    out[key] = value
  }
}

/**
 * Identificator.getData всей формы (skipStore / storeClientData=0 на видимых панелях).
 * Не берёт `<hidden>` и клоны Tiles. Bound `_id` услуги сюда не подмешивается — только hook getData.
 */
export function collectFormIdentificatorGetDataFields(
  elements: Record<string, unknown> | null | undefined,
  options?: FormLevelGetDataCollectOptions,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  const hiddenPanelIds = resolveHiddenPanelIds(options?.formRoot)
  for (const el of flattenFormLeafElements(elements, options?.extraMaps)) {
    const rec = el as Record<string, unknown>
    if (!registryEntryMatchesFormId(rec, options?.formId)) continue
    if (shouldSkipFormLevelLeafGetData(rec, hiddenPanelIds)) continue
    const hint = readPanelGetDataElementHint(el)
    if (!isIdentificatorElementType(hint.elementType)) continue
    const getData = rec.getData
    if (typeof getData !== 'function') continue
    assignGetDataMap(out, getData.call(el))
  }
  return out
}

/**
 * ListBox/ComboBox getData (или live scalar) всей формы — skipStore / не попало в getForm.
 */
export function collectFormListboxGetDataFields(
  elements: Record<string, unknown> | null | undefined,
  options?: FormLevelGetDataCollectOptions,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  const hiddenPanelIds = resolveHiddenPanelIds(options?.formRoot)
  for (const el of flattenFormLeafElements(elements, options?.extraMaps)) {
    const rec = el as Record<string, unknown>
    if (!registryEntryMatchesFormId(rec, options?.formId)) continue
    if (shouldSkipFormLevelLeafGetData(rec, hiddenPanelIds)) continue
    const hint = readPanelGetDataElementHint(el)
    const elType = String(hint.elementType ?? '').trim().toUpperCase()
    if (!FORM_LISTBOX_GET_DATA_TYPES.has(elType)) continue
    const getData = rec.getData
    if (typeof getData === 'function') {
      assignGetDataMap(out, getData.call(el))
      continue
    }
    const fieldName = String(hint.fieldName ?? rec.fieldName ?? '').trim()
    if (!fieldName) continue
    const live = rec.getLiveFieldValue
    if (typeof live === 'function') {
      const value = live.call(el)
      if (value !== undefined && value !== null) {
        out[fieldName] = value
      }
    }
  }
  return out
}

/** SwingText.getData: widget text after fill, including storeClientData=0 panels. */
export function collectFormTextGetDataFields(
  elements: Record<string, unknown> | null | undefined,
  options?: FormLevelGetDataCollectOptions,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  const hiddenPanelIds = resolveHiddenPanelIds(options?.formRoot)
  for (const el of flattenFormLeafElements(elements, options?.extraMaps)) {
    const rec = el as Record<string, unknown>
    if (!registryEntryMatchesFormId(rec, options?.formId)) continue
    if (shouldSkipFormLevelLeafGetData(rec, hiddenPanelIds)) continue
    const hint = readPanelGetDataElementHint(el)
    const elType = String(hint.elementType ?? '').trim().toUpperCase()
    if (!FORM_TEXT_GET_DATA_TYPES.has(elType)) continue
    const fieldName = String(hint.fieldName ?? rec.fieldName ?? '').trim()
    if (!fieldName) continue
    if (out[fieldName] != null && fieldEntryHasPresentableValue(out[fieldName])) continue
    const getData = rec.getData
    if (typeof getData === 'function') {
      assignPresentableGetDataMap(out, getData.call(el))
      continue
    }
    const live = rec.getLiveFieldValue
    if (typeof live === 'function') {
      const value = live.call(el)
      if (value !== undefined && value !== null && !fieldEntryLooksEmptyIncoming(value)) {
        out[fieldName] = value
      }
    }
  }
  return out
}

function unwrapFieldScalar(entry: unknown): unknown {
  if (entry && typeof entry === 'object' && !Array.isArray(entry) && 'value' in entry) {
    return (entry as { value: unknown }).value
  }
  return entry
}

function scalarEquals(left: unknown, right: unknown): boolean {
  const a = unwrapFieldScalar(left)
  const b = unwrapFieldScalar(right)
  if (a == null || b == null) return false
  return String(a) === String(b)
}

/**
 * Дописать Identificator + ListBox в снимок формы. Пустой incoming не затирает уже собранное.
 * Не подставляет document `_id` услуги в чужие Identificator-имена.
 */
export function applyFormIdentificatorAndListboxGetDataToFields(
  fields: Record<string, unknown>,
  elements: Record<string, unknown> | null | undefined,
  options?: FormLevelGetDataCollectOptions,
): void {
  const incoming = {
    ...collectFormIdentificatorGetDataFields(elements, options),
    ...collectFormListboxGetDataFields(elements, options),
    ...collectFormTextGetDataFields(elements, options),
  }
  const boundId = fields._id
  for (const [key, value] of Object.entries(incoming)) {
    if (value === undefined || value === null) continue
    if (shouldPreserveFieldEntryOnEmptyIncoming(fields[key], value)) continue
    if (
      !LAYOUT_IDENTITY_FIELD_KEYS.has(key) &&
      boundId != null &&
      scalarEquals(value, boundId) &&
      fieldEntryHasPresentableValue(fields[key]) &&
      !scalarEquals(fields[key], boundId)
    ) {
      continue
    }
    fields[key] = value
  }
}
